/**
 * The instanced bit renderer: one quad per bit instance, every state an SDF
 * shape in the fragment shader, and the glow as the same instances drawn
 * again at 1.8× with additive falloff.
 *
 * Uploads follow the packer: only the instance ranges a pack rewrote go to
 * the GPU (`bufferSubData`), unless this pass missed a frame, grew, or came
 * back from a lost context, when it sends everything once.
 */

import type { FrameModel } from '../contracts';
import { BIT, type PackedFrame } from './frame';
import type { GlKit } from './gl';
import type { Rgb, Theme } from './palette';
import vert from '../shaders/bits.vert.glsl' with { type: 'text' };
import frag from '../shaders/bits.frag.glsl' with { type: 'text' };
import glowFrag from '../shaders/glow.frag.glsl' with { type: 'text' };

export const GLOW_PAD = 3.2;
/** The uniforms each program is driven by (the shader checks hold the sources to these). */
export const BITS_UNIFORMS = ['u_camera', 'u_glowPass', 'u_pad', 'u_freeFill', 'u_refuse', 'u_focus', 'u_bg', 'u_ink', 'u_time', 'u_radius'] as const;
export const GLOW_UNIFORMS = ['u_camera', 'u_glowPass', 'u_pad', 'u_strength'] as const;
export const BITS_SOURCES = { vert, frag, glow: glowFrag } as const;

export interface BitsPass {
  upload(frame: FrameModel): void;
  draw(frame: FrameModel, theme: Theme, time: number): number;
  drawGlow(frame: FrameModel, strength: number): number;
  forceFull(): void;
  destroy(): void;
}

const isPacked = (f: FrameModel): f is PackedFrame => 'dirty' in f && 'version' in f;

/** Which byte ranges to send for a frame: all of it, or the dirty ranges (pure, tested). */
export const uploadPlan = (frame: FrameModel, state: { capacity: number; version: number; full: boolean }): { grow: number | null; ranges: [number, number][] } => {
  const count = frame.bitCount;
  const grow = frame.bits.length > state.capacity ? frame.bits.length : null;
  if (grow !== null || state.full || !isPacked(frame) || frame.version !== state.version + 1) {
    return { grow, ranges: count > 0 ? [[0, count]] : [] };
  }
  return { grow: null, ranges: frame.dirty.filter(([a, b]) => b > a).map(([a, b]) => [a, b] as [number, number]) };
};

export const createBitsPass = (gl: WebGL2RenderingContext, kit: GlKit): BitsPass => {
  const prog = kit.link(vert, frag, 'bits');
  const glow = kit.link(vert, glowFrag, 'glow');
  const u = kit.uniforms(prog, BITS_UNIFORMS);
  const ug = kit.uniforms(glow, GLOW_UNIFORMS);
  const vao = gl.createVertexArray();
  const buf = gl.createBuffer();
  const stride = BIT.floats * 4;
  gl.bindVertexArray(vao);
  kit.bindQuad(0);
  gl.bindBuffer(gl.ARRAY_BUFFER, buf);
  for (const [loc, off] of [[1, 0], [2, 16], [3, 32]] as const) {
    gl.enableVertexAttribArray(loc);
    gl.vertexAttribPointer(loc, 4, gl.FLOAT, false, stride, off);
    gl.vertexAttribDivisor(loc, 1);
  }
  gl.bindVertexArray(null);
  const state = { capacity: 0, version: -1, full: true };
  const set3 = (loc: WebGLUniformLocation | null, c: Rgb) => gl.uniform3f(loc, c[0], c[1], c[2]);

  return {
    upload(frame) {
      gl.bindBuffer(gl.ARRAY_BUFFER, buf);
      const plan = uploadPlan(frame, state);
      if (plan.grow !== null) {
        gl.bufferData(gl.ARRAY_BUFFER, plan.grow * 4, gl.DYNAMIC_DRAW);
        state.capacity = plan.grow;
      }
      for (const [a, b] of plan.ranges) {
        const from = a * BIT.floats, len = (b - a) * BIT.floats;
        if (from + len > state.capacity) continue;
        gl.bufferSubData(gl.ARRAY_BUFFER, from * 4, frame.bits, from, len);
      }
      state.full = false;
      state.version = isPacked(frame) ? frame.version : -1;
    },
    draw(frame, theme, time) {
      if (frame.bitCount <= 0) return 0;
      gl.useProgram(prog);
      gl.bindVertexArray(vao);
      gl.uniformMatrix4fv(u.u_camera, false, frame.camera.subarray(0, 16));
      gl.uniform1f(u.u_glowPass, 0);
      gl.uniform1f(u.u_pad, 1);
      set3(u.u_freeFill, theme.freeFill);
      set3(u.u_refuse, theme.refuse);
      set3(u.u_focus, theme.focus);
      set3(u.u_bg, theme.bg);
      set3(u.u_ink, theme.ink);
      gl.uniform1f(u.u_time, time);
      gl.uniform1f(u.u_radius, 2);
      gl.drawArraysInstanced(gl.TRIANGLE_STRIP, 0, 4, frame.bitCount);
      gl.bindVertexArray(null);
      return 1;
    },
    drawGlow(frame, strength) {
      if (frame.bitCount <= 0) return 0;
      gl.useProgram(glow);
      gl.bindVertexArray(vao);
      gl.uniformMatrix4fv(ug.u_camera, false, frame.camera.subarray(0, 16));
      gl.uniform1f(ug.u_glowPass, 1);
      gl.uniform1f(ug.u_pad, GLOW_PAD);
      gl.uniform1f(ug.u_strength, strength);
      gl.drawArraysInstanced(gl.TRIANGLE_STRIP, 0, 4, frame.bitCount);
      gl.bindVertexArray(null);
      return 1;
    },
    forceFull() {
      state.full = true;
    },
    destroy() {
      gl.deleteBuffer(buf);
      gl.deleteVertexArray(vao);
      gl.deleteProgram(prog);
      gl.deleteProgram(glow);
    },
  };
};
